/**
 * The ONLY comps an EXISTING template field may be changed BETWEEN, offered as
 * swap targets by both in-place type-change surfaces:
 *   - reimport (DirectDataImport, existing template fields only)
 *   - the listing-header pencil (ChangeFieldCompDropdown)
 *
 * MUST stay a subset of the BE allowlist: okf-be resolveFieldCompSwap requires
 * BOTH the stored comp and the new comp to be in
 * compsAvailableForDirectDataImport, and a disallowed swap makes the
 * changeFieldComp endpoint THROW (both surfaces PATCH it — reimport fires its
 * type changes through it BEFORE the import). So offering anything outside the
 * BE list is an error banner. This list is deliberately NARROWER than the BE's:
 * it's the product decision of what to offer, not what the BE can do.
 *
 * Deliberately excluded, and why:
 *   - tag inputs — a swap to one needs a tag template created; the standalone
 *     service passes `tagTplsToCreate: []`, so the field would point at a tag
 *     collection that never exists.
 *   - Document Title (TitleInput) — stays as-is by design.
 *   - SimpleTable — keeps the old locked behaviour.
 *
 * NOT a gate on what a column can be IMPORTED as — that's
 * compsAvailableForDirectDataImport (see useDirectImportCompOptions), which
 * stays the single source of truth for new columns and is not narrowed by this.
 */
export const SWAPPABLE_FIELD_COMPS = [
  "TextInput", // Text
  "DropdownMulti", // Multi Select
  "DropdownSingle", // Single Select
  "NumberInput", // Number
  "DatePicker", // Date
  "DateTimePicker", // Date & Time — swaps with DatePicker migrate data losslessly (same stored timestamp)
  "URLInput", // URL
  "JSONInput", // JSON Input
  "LexicalTextEditor", // Rich Text Field
];

export const SWAPPABLE_FIELD_COMPS_SET = new Set(SWAPPABLE_FIELD_COMPS);

/**
 * Which swaps actually carry their data across, mirroring the BE migrator's comp
 * families (okf-be convertFieldValue.js — keep in sync).
 *
 * It only migrates a pair in the SAME family. Anything else, including
 * cross-family (e.g. Text → Date), enqueues no migration at all: the old values
 * stay orphaned at the old valuePath and the column reads empty.
 *
 * Membership must be tested as a PAIR, not as two lookups in the union — Text
 * and DatePicker are both migratable comps, just not with each other.
 */
const SELECT_FAMILY = new Set(["TextInput", "DropdownSingle", "DropdownMulti"]);
const DATE_FAMILY = new Set(["DatePicker", "DateTimePicker"]);
const COMP_FAMILIES = [SELECT_FAMILY, DATE_FAMILY];

const isSameFamilyPair = (fromComp, toComp) =>
  COMP_FAMILIES.some((f) => f.has(fromComp) && f.has(toComp));

/**
 * @returns null when the swap keeps its data, else the severity of the loss —
 *   which picks the warning's colour and copy (fieldTypeChangeWarningConfig).
 */
export const getFieldTypeChangeDataLoss = ({ fromComp, toComp }) => {
  if (!fromComp || !toComp || fromComp === toComp) return null;

  if (!isSameFamilyPair(fromComp, toComp)) {
    return { severity: "total" };
  }

  // The one lossy conversion the migrator DOES perform: it keeps the first
  // option and drops the rest (convertFieldValue.js, Multi → Single branch).
  if (fromComp === "DropdownMulti" && toComp === "DropdownSingle") {
    return { severity: "partial" };
  }

  return null;
};
